import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AmountDisplay } from '../../../components/ui/AmountDisplay';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Button } from '../../../components/ui/Button';
import {
 useExitRequest,
 useComputeSettlement,
 useDownloadNoc,
} from '../hooks';
import { useToast } from '../../../components/ui/Toast';
import type { SubscriberListItem } from '../../schemes/network/schemes.api';
import type { SchemeListItem } from '../../schemes/network/schemes.api';

interface ForeclosureDetailDrawerProps {
 isOpen: boolean;
 exitRequestId: string | null;
 onClose: () => void;
 subscribers: SubscriberListItem[];
 currentScheme: SchemeListItem | null;
}

const mapStatusToBadge = (status: string): 'success' | 'warning' | 'danger' | 'info' => {
 switch (status) {
 case 'APPROVED':
 case 'REFUND_INITIATED':
 case 'NOC_ISSUED':
 return 'success';
 case 'UNDER_REVIEW':
 case 'SETTLEMENT_COMPUTED':
 return 'warning';
 case 'REJECTED':
 return 'danger';
 case 'REQUESTED':
 default:
 return 'info';
 }
};

export function ForeclosureDetailDrawer({
 isOpen,
 exitRequestId,
onClose,
  subscribers,
  currentScheme,
}: ForeclosureDetailDrawerProps) {
 const queryClient = useQueryClient();
 const { toast } = useToast();

 const { data: request, isLoading, error } = useExitRequest(isOpen ? (exitRequestId ?? '') : '');
 const computeMutation = useComputeSettlement();
 const downloadNocMutation = useDownloadNoc();

 const subscriber = React.useMemo(() => {
 if (!request || !subscribers) return null;
 return subscribers.find((s) => s.id === request.subscriberId);
 }, [request, subscribers]);

 const handleCompute = () => {
 if (!exitRequestId) return;
 computeMutation.mutate(exitRequestId);
 };

 const handleDownloadNoc = () => {
 if (!exitRequestId) return;
 downloadNocMutation.mutate(exitRequestId, {
 onSuccess: (data) => {
 if (data?.documentRef) {
 window.open(data.documentRef, '_blank');
 } else {
 toast({
 title: 'NOC document not ready',
 description: 'The document is not available yet or is still generating.',
 type: 'info',
 });
 }
 },
 });
 };

 if (!isOpen) return null;

 return (
 <>
 <div
 className="fixed inset-0 z-modal overflow-hidden"
 role="dialog"
 aria-modal="true"
 aria-labelledby="foreclosure-drawer-title"
 >
 <div className="absolute inset-0 overflow-hidden">
 {/* Backdrop */}
 <div
 onClick={onClose}
 className="absolute inset-0 bg-black/50 transition-opacity"
 aria-hidden="true"
 />

 <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
 <div className="pointer-events-auto w-screen max-w-lg bg-surface-1 border-l border-border shadow-2xl flex flex-col h-full">
 {/* Header */}
 <div className="px-6 py-5 bg-surface-2 border-b border-border flex items-center justify-between flex-shrink-0">
 <h2 id="foreclosure-drawer-title" className="text-lg font-bold text-text-primary">
 Foreclosure / Exit Detail
 </h2>
 <Button
 variant="ghost"
 onClick={onClose}
 className="text-text-secondary hover:text-text-primary p-1 rounded-full hover:bg-surface-3 transition-colors"
 aria-label="Close"
 >
 <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
 </svg>
 </Button>
 </div>

 {/* Body */}
 <div className="flex-1 overflow-y-auto p-6 space-y-6">
 {isLoading && (
 <div className="flex items-center justify-center h-48 text-text-muted text-sm">
 Loading exit request details…
 </div>
 )}

 {error && (
 <div className="p-4 bg-danger-bg text-danger-text rounded-lg text-sm">
 Failed to load exit request details.
 </div>
 )}

 {request && (
 <>
 {/* General Request Info */}
 <div className="space-y-4">
 <div className="flex justify-between items-center">
 <span className="text-xs font-bold text-text-secondary uppercase tracking-wider">Status</span>
 <StatusBadge
 status={mapStatusToBadge(request.status)}
 label={request.status.replace(/_/g, ' ')}
 />
 </div>

 <div className="bg-surface-2 rounded-xl border border-border p-4 space-y-3">
 <div className="flex justify-between text-sm">
 <span className="text-text-secondary">Customer Name</span>
 <span className="font-semibold text-text-primary">
 {subscriber?.customerName || request.customerName || 'N/A'}
 </span>
 </div>
 <div className="flex justify-between text-sm">
 <span className="text-text-secondary">Ticket Number(s)</span>
 <span className="font-semibold text-text-primary">
 {subscriber?.ticketNumbers?.join(', ') || 'N/A'}
 </span>
 </div>
 <div className="flex justify-between text-sm">
 <span className="text-text-secondary">Exit Type</span>
 <span className="font-semibold text-text-primary text-xs uppercase bg-surface-3 px-2 py-0.5 rounded-[4px]">
 {request.exitType.replace(/_/g, ' ')}
 </span>
 </div>
 <div className="flex justify-between text-sm">
 <span className="text-text-secondary">Requested On</span>
 <span className="font-semibold text-text-primary">
 {new Date(request.requestDate).toLocaleDateString()}
 </span>
 </div>
 </div>
 </div>

{/* Subscriber Statistics - Spec 4.1 Requirement */}
  <div>
  <h3 className="text-xs font-bold text-text-secondary uppercase tracking-wider mb-3">
  Subscriber Stats
  </h3>
  <div className="grid grid-cols-3 gap-3">
  <div className="bg-surface-2 border border-border rounded-xl p-3 text-center">
  <p className="text-xs text-text-secondary">Cycles Completed</p>
  <p className="text-base font-bold text-text-primary mt-1">
  {subscriber?.prizedAt ? 'Full' : `${currentScheme?.currentCycle ?? '—'} / ${currentScheme?.durationCycles ?? '—'}`}
  </p>
  </div>
  <div className="bg-surface-2 border border-border rounded-xl p-3 text-center">
  <p className="text-xs text-text-secondary">Total Paid</p>
  <p className="text-sm font-bold text-text-primary mt-1">
  {request.settlementBreakdown ? (
  <AmountDisplay paise={request.settlementBreakdown.contributions} />
  ) : (
  <span className="text-text-muted">—</span>
  )}
  </p>
  </div>
  <div className="bg-surface-2 border border-border rounded-xl p-3 text-center">
  <p className="text-xs text-text-secondary">Total Dividend</p>
  <p className="text-sm font-bold text-text-primary mt-1">
  {request.settlementBreakdown ? (
  <AmountDisplay paise={request.settlementBreakdown.dividends} />
  ) : (
  <span className="text-text-muted">—</span>
  )}
  </p>
  </div>
  </div>
  </div>

 {/* Actions and Breakdown */}
 <div className="space-y-4 pt-2 border-t border-border">
 {request.status === 'REQUESTED' || request.status === 'UNDER_REVIEW' ? (
 <div className="space-y-3">
 <p className="text-xs text-text-secondary leading-relaxed">
 Before approving or rejecting this premature exit, you must calculate the settlement quote.
 </p>
 <Button
 onClick={handleCompute}
 loading={computeMutation.isPending}
 className="w-full justify-center"
 >
 Compute Settlement Quote
 </Button>
 </div>
 ) : null}

 {request.status !== 'REQUESTED' && request.status !== 'UNDER_REVIEW' && (
 <div className="space-y-4">
 <h3 className="text-xs font-bold text-text-secondary uppercase tracking-wider">
 Settlement Quote Breakdown
 </h3>

 <div className="bg-surface-2 rounded-xl border border-border divide-y divide-border overflow-hidden">
 <div className="px-4 py-3 flex justify-between items-center text-sm">
 <span className="text-text-secondary font-medium">Gross Paid Contributions</span>
 <span className="font-bold text-text-primary">
 <AmountDisplay paise={request.settlementBreakdown?.contributions ?? 0} />
 </span>
 </div>
 {(request.settlementBreakdown?.advanceBalance ?? 0) > 0 && (
 <div className="px-4 py-3 flex justify-between items-center text-sm">
 <span className="text-text-secondary font-medium">Advance / Unallocated Balance</span>
 <span className="font-bold text-success-text">
 +<AmountDisplay paise={request.settlementBreakdown?.advanceBalance ?? 0} />
 </span>
 </div>
 )}
 <div className="px-4 py-3 flex justify-between items-center text-sm">
 <span className="text-text-secondary font-medium">Dividends Received</span>
 <span className="font-bold text-success-text">
 +<AmountDisplay paise={request.settlementBreakdown?.dividends ?? 0} />
 </span>
 </div>
 <div className="px-4 py-3 flex justify-between items-center text-sm">
 <span className="text-text-secondary font-medium">Foreclosure Charges / Penalty</span>
 <span className="font-bold text-danger-text">
 -<AmountDisplay paise={request.foremanDeduction ?? 0} />
 </span>
 </div>
 {(request.settlementBreakdown?.defaultBalanceNetted ?? 0) > 0 && (
 <div className="px-4 py-3 flex justify-between items-center text-sm">
 <span className="text-text-secondary font-medium">Open Default Balance Recovered</span>
 <span className="font-bold text-danger-text">
 -<AmountDisplay paise={request.settlementBreakdown?.defaultBalanceNetted ?? 0} />
 </span>
 </div>
 )}
 <div className="px-4 py-3 bg-surface-3 flex justify-between items-center text-sm">
 <span className="text-text-primary font-bold">Net Payout Amount</span>
 <span className="font-extrabold text-text-primary text-base">
 <AmountDisplay paise={request.settlementAmount ?? 0} />
 </span>
 </div>
 </div>

 <div className="p-3 bg-warning-bg border border-warning-200 rounded-sm text-xs text-warning-text font-medium">
 ⚠️ Note: Contributions reflect posted receipts. Ensure all pending digital/cheque collections are reconciled prior to approving payout.
 </div>

 {request.status === 'SETTLEMENT_COMPUTED' && (
 <div className="pt-2">
 <Link
 to="/approvals"
 className="text-sm text-accent-600 hover:text-accent-500 hover:underline font-medium"
 >
 Awaiting settlement approval — review in Approvals queue →
 </Link>
 </div>
 )}

 {['APPROVED', 'REFUND_INITIATED', 'NOC_ISSUED'].includes(request.status) && (
 <div className="space-y-3 pt-2">
 <div className="p-4 bg-success-50 border border-success-500/20 rounded-xl flex items-start gap-3">
 <svg className="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
 </svg>
 <div>
 <p className="text-sm font-bold text-success-900">Exit Request Approved</p>
 <p className="text-xs text-success-700 mt-1">
 The subscriber has been processed for exit. A refund of{' '}
 <span className="font-bold">
 <AmountDisplay paise={request.settlementAmount ?? 0} />
 </span>{' '}
 is scheduled.
 </p>
 </div>
 </div>

 {request.refundInstructionId && (
 <a
 href="/instructions"
 className="w-full text-center inline-flex items-center justify-center gap-1 text-xs font-bold text-accent-500 hover:text-accent-600 underline underline-offset-2 p-2 border border-border rounded-lg bg-surface-2 transition-colors hover:bg-surface-3"
 >
 View Refund in Instructions Queue →
 </a>
 )}

 {['APPROVED', 'NOC_ISSUED'].includes(request.status) && (
 <Button
 variant="secondary"
 onClick={handleDownloadNoc}
 loading={downloadNocMutation.isPending}
 className="w-full justify-center mt-2 border-accent-500 text-accent-500"
 >
 Download NOC Document (PDF)
 </Button>
 )}
 </div>
 )}

 {request.status === 'REJECTED' && (
 <div className="p-4 bg-danger-bg/10 border border-danger-text/20 rounded-xl">
 <p className="text-sm font-bold text-danger-text">Exit Request Rejected</p>
 <p className="text-xs text-danger-text mt-1">
 <span className="font-semibold">Reason:</span> {request.rejectionReason || 'No reason provided.'}
 </p>
 </div>
 )}
 </div>
 )}
 </div>
 </>
 )}
 </div>
 </div>
 </div>
 </div>
 </div>
 </>
 );
}
